import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { saveReadingPosition } from './readingPosition';

export default function useReadingPosition({ containerRef, ready, content, section, type, bookName }) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const container = containerRef.current;
    // English editions use table rows instead of the Japanese verse markers.
    const markers = Array.from(container.querySelectorAll('em[id], tr')).map((element) => {
      const match = element.tagName === 'TR'
        ? element.querySelector('td.vn')?.textContent.trim().match(/^(\d+):(\d+)$/)
        : element.id.match(/^(\d+)-(\d+)$/);
      return match ? { element, chapter: Number(match[1]), verse: Number(match[2]) } : null;
    }).filter(Boolean);
    if (!markers.length) return;

    let lastSaved = '';
    let timer;
    const save = (marker) => {
      const key = `${marker.chapter}-${marker.verse}`;
      if (lastSaved === key) return;
      lastSaved = key;
      saveReadingPosition({ section, type, bookName, chapter: marker.chapter, verse: marker.verse });
    };
    const capture = () => {
      let current = markers[0];
      for (const marker of markers) {
        if (marker.element.getBoundingClientRect().top > 160) break;
        current = marker;
      }
      save(current);
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(capture, 180);
    };
    const frame = requestAnimationFrame(() => {
      const resume = hash.match(/^#resume=(\d+)-(\d+)$/);
      const target = resume && markers.find((marker) => marker.chapter === Number(resume[1]) && marker.verse === Number(resume[2]));
      if (target) {
        window.scrollTo({ top: Math.max(0, target.element.getBoundingClientRect().top + window.scrollY - 150), behavior: 'instant' });
        save(target);
      } else {
        capture();
      }
    });
    const onHide = () => {
      if (document.visibilityState === 'hidden') capture();
    };
    const onNavigate = (event) => {
      if (event.target.closest?.('a[href]')) capture();
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('pagehide', capture);
    document.addEventListener('visibilitychange', onHide);
    document.addEventListener('click', onNavigate, true);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('pagehide', capture);
      document.removeEventListener('visibilitychange', onHide);
      document.removeEventListener('click', onNavigate, true);
    };
  }, [containerRef, ready, content, section, type, bookName, hash]);
}
